import Form from "./modules/form";
import EntryCollector from "./modules/entry-collector";
import CookieConsent from "./modules/cookie-consent";
import Countdown from "./modules/countdown";
import MasonryGallery from "./modules/masonry-gallery";
import Slider from "./modules/slider";
import { Alert } from "bootstrap";

const LoadingScreen = {
  show: () => {
    document.body.style.overflow = "hidden";
    document.getElementById("loading-screen").classList.remove("d-none");
  },
  hide: () => {
    document.getElementById("loading-screen").classList.add("d-none");
    document.body.style.overflow = "";
  },
};

const triggerAlert = (
  alertId,
  alertContent = null,
  alertFunction = null
) => {
  const alertTargetEl = document.getElementById(alertId);

  if (!alertTargetEl) {
    return false;
  }

  const alertEl = alertTargetEl.cloneNode(true);
  alertEl.id = "";

  alertTargetEl.parentNode.append(alertEl);

  if (alertContent !== null) {
    const contentElement = alertEl.querySelector("div");

    if (contentElement) {
      // Der Text kommt entweder vom Controller oder aus eigener JS-Validierung.
      // textContent statt innerHTML verhindert HTML-Injection durch Dateinamen.
      contentElement.textContent = alertContent;
    }
  }

  const bsAlert = new Alert(alertEl);

  alertEl.classList.remove("visually-hidden");

  setTimeout(() => {
    alertEl.classList.add("show");
  }, 100);

  const alertAutoclose = alertEl.dataset?.alertAutoclose;

  if (alertAutoclose) {
    setTimeout(() => {
      bsAlert.close();
    }, Number(alertAutoclose));
  }

  if (alertFunction !== null) {
    alertFunction();
  }

  return true;
};

CookieConsent.init();
EntryCollector.init();

document.querySelectorAll("[data-hide]").forEach((el) => {
  el.addEventListener("click", () => {
    const targetEl = el.closest(`.${el.dataset.hide}`);
    targetEl.classList.remove("show");
  });
});

document.querySelectorAll("[data-visually-hide]").forEach((el) => {
  el.addEventListener("click", () => {
    const targetEl = el.closest(`.${el.dataset.visuallyHide}`);
    targetEl.classList.remove("show");

    setTimeout(() => {
      targetEl.classList.add("visually-hidden");
    }, 150);
  });
});

document.querySelectorAll("[data-form]").forEach((form) => {
  new Form(`#${form.id}`);
});

// Click listener for clickable cards.
document.querySelectorAll(".card--clickable").forEach((node) => {
  node.addEventListener("click", (e) => {
    if (
      !e.target.classList.contains("btn-secondary") &&
      !e.target.parentNode.classList.contains("btn-secondary")
    ) {
      LoadingScreen.show();
    }

    if (
      e.target.tagName !== "A" &&
      !e.target.classList.contains("btn") &&
      e.target.parentNode.tagName !== "A" &&
      !e.target.parentNode.classList.contains("btn")
    ) {
      node.querySelector(".btn-primary").click();
    }
  });
});

document.querySelectorAll("[data-upload-form]").forEach((formElement) => {
  const uploadForm = new Form(`#${formElement.id}`);
  const fileInput = formElement.querySelector('input[type="file"]');
  const uploadProgressElement = formElement.querySelector("[data-upload-progress]");
  const uploadProgressBarElement = formElement.querySelector(
    "[data-upload-progress-bar]"
  );

  const setUploadProgress = (progressPercent) => {
    if (!uploadProgressElement || !uploadProgressBarElement) {
      return;
    }

    uploadProgressElement.classList.remove("visually-hidden");
    uploadProgressBarElement.style.width = `${progressPercent}%`;
    uploadProgressBarElement.setAttribute(
      "aria-valuenow",
      String(progressPercent)
    );
    uploadProgressBarElement.textContent = `${progressPercent}%`;
  };

  const resetUploadProgress = () => {
    if (!uploadProgressElement || !uploadProgressBarElement) {
      return;
    }

    uploadProgressElement.classList.add("visually-hidden");
    uploadProgressBarElement.style.width = "0%";
    uploadProgressBarElement.setAttribute("aria-valuenow", "0");
    uploadProgressBarElement.textContent = "0%";
  };

  uploadForm.preventDefaultSubmit();

  fileInput?.addEventListener("change", () => {
    const validation = uploadForm.validateFiles(fileInput);

    if (!validation.valid) {
      formElement.classList.add("was-validated");

      triggerAlert(
        uploadForm.getDataset("uploadFormAlertError"),
        validation.message
      );
    }
  });

  uploadForm.onSubmit((event, form) => {
    const validation = form.validateFiles(fileInput);

    if (!validation.valid) {
      event.preventDefault();
      form.addClass("was-validated");

      triggerAlert(
        form.getDataset("uploadFormAlertError"),
        validation.message
      );

      return;
    }

    const selectedFiles = Array.from(fileInput?.files || []);

    if (selectedFiles.length === 0) {
      triggerAlert(
        form.getDataset("uploadFormAlertError"),
        "Es wurden keine Dateien ausgewählt."
      );

      return;
    }

    const batchSize = 20;
    const fileFieldName = fileInput?.name || "uploadFile[]";
    const batches = [];

    for (let index = 0; index < selectedFiles.length; index += batchSize) {
      batches.push(selectedFiles.slice(index, index + batchSize));
    }

    const totalBytes = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    let uploadedBytes = 0;
    let successMessage = "Die Dateien wurden erfolgreich hochgeladen.";

    const baseFields = [];

    form
      .getElement()
      .querySelectorAll("input, textarea, select")
      .forEach((field) => {
        if (!field.name || field.disabled || field.type === "file") {
          return;
        }

        if ((field.type === "checkbox" || field.type === "radio") && !field.checked) {
          return;
        }

        baseFields.push([field.name, field.value]);
      });

    const createBatchFormData = (batchFiles) => {
      const batchFormData = new FormData();

      baseFields.forEach(([name, value]) => {
        batchFormData.append(name, value);
      });

      batchFiles.forEach((file) => {
        batchFormData.append(fileFieldName, file, file.name);
      });

      return batchFormData;
    };

    const updateOverallProgress = (currentBatchLoadedBytes = 0) => {
      if (totalBytes <= 0) {
        return;
      }

      const loadedBytes = Math.min(
        totalBytes,
        uploadedBytes + currentBatchLoadedBytes
      );

      const progressPercent = Math.round((loadedBytes / totalBytes) * 100);
      setUploadProgress(progressPercent);
    };

    const finalizeUpload = () => {
      form.enable();
      setTimeout(() => {
        resetUploadProgress();
      }, 500);
    };

    const triggerUploadError = (message, xhr = null) => {
      const errorAlertTriggered = triggerAlert(
        form.getDataset("uploadFormAlertError"),
        message
      );

      if (!errorAlertTriggered && xhr) {
        console.warn("Upload fehlgeschlagen:", {
          status: xhr.status,
          response: xhr.responseText,
        });
      }
    };

    const uploadBatch = (batchIndex = 0) => {
      if (batchIndex >= batches.length) {
        setUploadProgress(100);
        triggerAlert(form.getDataset("uploadFormAlertSuccess"), successMessage);
        form.reset();
        form.removeClass("was-validated");
        finalizeUpload();

        return;
      }

      const batchFiles = batches[batchIndex];
      const batchTotalBytes = batchFiles.reduce((sum, file) => sum + file.size, 0);
      const xhr = new XMLHttpRequest();

      xhr.upload.onloadstart = function () {
        updateOverallProgress(0);
      };

      xhr.upload.onprogress = function (progressEvent) {
        if (!progressEvent.lengthComputable) {
          return;
        }

        const currentBatchLoadedBytes = Math.min(
          batchTotalBytes,
          progressEvent.loaded
        );

        updateOverallProgress(currentBatchLoadedBytes);
      };

      xhr.upload.onload = function () {
        updateOverallProgress(batchTotalBytes);
      };

      xhr.onload = function () {
        const contentType = xhr.getResponseHeader("content-type") || "";
        let response = null;

        if (contentType.includes("json") && xhr.responseText) {
          try {
            response = JSON.parse(xhr.responseText);
          } catch (error) {
            console.error("Ungültige JSON-Antwort vom Upload-Endpunkt:", error);
          }
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          if (!response) {
            triggerUploadError("Der Server hat keine gültige Antwort geliefert.");
            finalizeUpload();

            return;
          }

          const { error = false, success = false } = response;

          if (error !== false) {
            triggerUploadError(error);
            finalizeUpload();

            return;
          }

          uploadedBytes += batchTotalBytes;
          updateOverallProgress(0);

          if (success) {
            successMessage = success;
          }

          uploadBatch(batchIndex + 1);

          return;
        }

        const errorMessage =
          response?.error ||
          (xhr.status === 413
            ? "Der Upload ist zu groß."
            : "Der Upload konnte nicht verarbeitet werden.");

        triggerUploadError(errorMessage, xhr);
        finalizeUpload();
      };

      xhr.onerror = function () {
        triggerUploadError(
          "Der Upload konnte wegen eines Netzwerkfehlers nicht übertragen werden."
        );
        finalizeUpload();
      };

      xhr.ontimeout = function () {
        triggerUploadError(
          "Der Upload hat zu lange gedauert und wurde abgebrochen."
        );
        finalizeUpload();
      };

      xhr.open(form.getMethod(), form.getAction(), true);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.send(createBatchFormData(batchFiles));
    };

    form.disable();
    setUploadProgress(0);
    uploadBatch();
  });
});

document.querySelectorAll("[data-countdown]").forEach((node) => {
  new Countdown(node);
});

document.querySelectorAll('[data-bs-toggle="modal"]').forEach((node) => {
  if (node.dataset.bsTarget === "#modal-upload-form") {
    node.addEventListener("click", () => {
      document
        .getElementById("modal-upload-form")
        .querySelector("form")
        .setAttribute("action", node.getAttribute("href"));
    });
  }
});

document.querySelectorAll("[data-download]").forEach((node) => {
  node.addEventListener("click", (e) => {
    e.preventDefault();

    if (!node.classList.contains("disabled")) {
      const iconNode = node.querySelector("i");
      const iconName = iconNode.innerText;
      const downloadUrl = node.getAttribute("href");

      node.classList.add("disabled");
      node.dataset.loading = true;
      iconNode.innerText = "downloading";

      fetch(downloadUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const tempEl = document.createElement("a");

          tempEl.href = window.URL.createObjectURL(blob);
          tempEl.download =
            node.getAttribute("download") || downloadUrl.split("/").pop();

          tempEl.click();
        })
        .then(() => {
          iconNode.innerText = iconName;
          node.classList.remove("disabled");
          node.dataset.loading = false;
          LoadingScreen.hide();
        });
    }
  });

  return false;
});

document
  .querySelectorAll("[data-loading]:not([data-download])")
  .forEach((node) => {
    node.addEventListener("click", () => {
      node.dataset.loading = true;
    });
  });

document
  .querySelectorAll(".mm-masonry")
  .forEach((container) => new MasonryGallery(container));

document
  .querySelectorAll(".carousel:not([data-slider-init='true'])")
  .forEach((node) => new Slider(node));

LoadingScreen.show();

document.querySelectorAll("[data-loading-screen]").forEach((node) => {
  node.addEventListener("click", () => {
    LoadingScreen.show();
  });
});

document.querySelectorAll("[data-history-back]").forEach((node) => {
  node.addEventListener("click", (e) => {
    const fallbackUrl = node.getAttribute("href");
    const hasHistory = window.history.length > 1;
    const sameOriginReferrer =
      !!document.referrer &&
      document.referrer.startsWith(window.location.origin);

    if (hasHistory && sameOriginReferrer) {
      e.preventDefault();
      window.history.back();
      return false;
    }

    if (!fallbackUrl) {
      e.preventDefault();
      window.location.assign("/");
      return false;
    }

    return true;
  });
});

document.addEventListener("readystatechange", (event) => {
  if (event.target.readyState === "complete") {
    LoadingScreen.hide();
  }
});

window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    LoadingScreen.hide();
  }
});
